import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import { formatNum } from "./DataTable";
import "./report.scss";
import { toFormatPrice } from "../helpers/function";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NoData from "./NoData";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGINATION = {
  total: 0,
  page: 1,
  limit: 10,
  total_pages: 1,
  has_next: false,
  has_prev: false,
};

function mapMetrics(summary = {}) {
  return [
    { label: "Item", value: formatNum(summary.item) },
    { label: "Qty", value: formatNum(summary.qty) },
    { label: "Sales Amount", value: toFormatPrice(Number(summary.sales_amount || 0)) },
    {
      label: "Average Sale per Item",
      value: toFormatPrice(Number(summary.avg_sale_per_item || 0)),
    },
  ];
}

function toMonthValueMap(months = []) {
  const out = {};
  months.forEach((m) => {
    out[m.key] = Number(m.qty) || 0;
  });
  return out;
}

function mapRows(data = []) {
  return data.map((row, idx) => ({
    _rowKey: row.price_range_key || `range-${idx}`,
    priceRange: row.price_range || "-",
    totalSales: Number(row.total_sales) || 0,
    months: toMonthValueMap(row.months),
    children: Array.isArray(row.children)
      ? row.children.map((c, cIdx) => ({
          _id: `${row.price_range_key || idx}-${c.item_name || cIdx}`,
          itemName: c.item_name || "-",
          totalSales: Number(c.total_sales) || 0,
          months: toMonthValueMap(c.months),
        }))
      : [],
  }));
}

function normalizeFilters(filters = {}) {
  const priceRangeOptions = Array.isArray(filters.price_range)
    ? filters.price_range.map((p) => (typeof p === "string" ? p : p.label))
    : [];
  return {
    location: Array.isArray(filters.location) ? filters.location : [],
    price_range: priceRangeOptions,
    items: Array.isArray(filters.items)
      ? filters.items
      : Array.isArray(filters.item)
        ? filters.item
        : [],
  };
}

function normalizeQueryParams(query = {}) {
  const next = { ...query };
  if (Array.isArray(next.item) && !Array.isArray(next.items)) {
    next.items = next.item;
    delete next.item;
  }
  return next;
}

function formatMonthQty(value) {
  const n = Number(value) || 0;
  return n === 0 ? "-" : formatNum(n);
}

const priceRangeTableStyles = {
  headCell: {
    borderTop: "1px solid #E3E3E3",
    borderBottom: "2px solid #E3E3E3",
  },
  totalCell: {
    borderTop: "1px solid #E3E3E3",
    borderBottom: "1px solid #E3E3E3",
  },
  bodyCell: {
    borderBottom: "1px solid #E3E3E3",
  },
};


const PriceRange = () => {
  const { t } = useTranslation()
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [months, setMonths] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [pageSize, setPageSize] = useState(10);

  const lastQueryRef = useRef({});
  const isFirstFetchRef = useRef(true);

  const fetchSalesByPriceRange = (params = {}) => {
    const merged = normalizeQueryParams({ ...lastQueryRef.current, ...params });
    lastQueryRef.current = merged;

    if (isFirstFetchRef.current) setLoading(true);

    Apiauth(merged, "Inventory/reports/salesByPriceRange")
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setMetrics(mapMetrics(res.summary));
          setMonths(Array.isArray(res.months) ? res.months : []);
          setFilters(normalizeFilters(res.filters ?? {}));
          setPagination(res.pagination ?? DEFAULT_PAGINATION);
        }
      })
      .finally(() => {
        setLoading(false);
        isFirstFetchRef.current = false;
      });
  };

  useEffect(() => {
    fetchSalesByPriceRange({ page: 1, limit: 10 });
  }, []);

  const monthKeys = useMemo(() => months.map((m) => m.key), [months]);
  const activeMonthKeys = monthKeys.length > 0 ? monthKeys : ["__month__"];
  const indexColWidth = 40;
  const priceRangeColWidth = 200;
  const totalSalesColWidth = 120;
  const monthColWidth = 95;
  const priceRangeTableMinWidth =
    indexColWidth +
    priceRangeColWidth +
    totalSalesColWidth +
    activeMonthKeys.length * monthColWidth;

  const totalsByMonth = useMemo(() => {
    const map = {};
    activeMonthKeys.forEach((k) => {
      map[k] = rows.reduce(
        (sum, row) => sum + (Number(row.months?.[k]) || 0),
        0,
      );
    });
    return map;
  }, [rows, activeMonthKeys]);

  const displayRows = useMemo(() => {
    const flat = [];
    rows.forEach((row, idx) => {
      flat.push({
        ...row,
        _isChild: false,
        _rowIndex: idx + 1,
      });
      if (expandedRows[row._rowKey] && Array.isArray(row.children)) {
        row.children.forEach((child) => {
          flat.push({
            _rowKey: child._id,
            _parentKey: row._rowKey,
            _isChild: true,
            itemName: child.itemName,
            totalSales: child.totalSales,
            months: child.months,
          });
        });
      }
    });
    return flat;
  }, [rows, expandedRows]);

  const page = pagination.page || 1;
  const limit = pagination.limit || 10;
  const totalRecords = pagination.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
  const rangeStart = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, totalRecords);

  function toggleRow(rowKey) {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }

  function goToPage(nextPage, nextLimit = limit) {
    fetchSalesByPriceRange({
      page: nextPage,
      limit: nextLimit,
    });
  }

  return (
    <>
      <Header title="Sales by Price Range" metrics={metrics} />
      <SubHeader
        filters={filters}
        onFilterApply={(query) =>
          fetchSalesByPriceRange({
            ...query,
            page: 1,
            limit: pagination.limit || 10,
          })
        }
      />

      {loading ? (
        <TableLoader />
      ) : (
        <div className="dtbs-wrapper">
          <div
            className="dtbs-table-container"
            style={{ overflowX: "auto", overflowY: "hidden" }}
          >
            <table
              className="dtbs-table"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: priceRangeTableMinWidth,
              }}
            >
              <colgroup>
                <col style={{ width: indexColWidth }} />
                <col style={{ width: priceRangeColWidth }} />
                <col style={{ width: totalSalesColWidth }} />
                {activeMonthKeys.map((key) => (
                  <col key={key} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="dtbs-th-center"
                    rowSpan={2}
                    style={priceRangeTableStyles.headCell}
                  >
                    #
                  </th>
                  <th
                    className="dtbs-th-left"
                    rowSpan={2}
                    style={priceRangeTableStyles.headCell}
                  >
                    {t("Price Range")}
                  </th>
                  <th
                    className="dtbs-th-right"
                    rowSpan={2}
                    style={{
                      ...priceRangeTableStyles.headCell,
                      borderRight: "1px solid rgb(233 233 233)",
                    }}
                  ></th>
                  <th
                    className="dtbs-th-center"
                    colSpan={activeMonthKeys.length}
                    style={priceRangeTableStyles.headCell}
                  >
                    {t("Qty")}
                  </th>
                </tr>
                <tr>
                  {months.length > 0 ? (
                    months.map((m) => (
                      <th
                        key={m.key}
                        className="dtbs-th-center"
                        style={priceRangeTableStyles.headCell}
                      >
                        {t(m.month_name)}
                      </th>
                    ))
                  ) : (
                    <th
                      className="dtbs-th-center"
                      style={priceRangeTableStyles.headCell}
                    >
                      {t("Month")}
                    </th>
                  )}
                </tr>
                <tr>
                  <td
                    className="dtbs-total-cell dtbs-td-left"
                    colSpan={2}
                    style={priceRangeTableStyles.totalCell}
                  >
                    {t("Total")}
                  </td>
                  <td
                    className="dtbs-total-cell dtbs-total-cell-right"
                    style={{
                      ...priceRangeTableStyles.totalCell,
                      borderRight: "1px solid rgb(233 233 233)",
                    }}
                  >
                    {toFormatPrice(
                      rows.reduce((s, r) => s + (Number(r.totalSales) || 0), 0),
                    )}
                  </td>
                  {activeMonthKeys.map((k) => (
                    <td
                      key={`total-${k}`}
                      className="dtbs-total-cell dtbs-td-center"
                      style={priceRangeTableStyles.totalCell}
                    >
                      {formatMonthQty(t(totalsByMonth[k]))}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3 + activeMonthKeys.length}
                      style={{ padding: "0" }}
                    >
                      <NoData />
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row, idx) => {
                    const hasChildren =
                      !row._isChild &&
                      Array.isArray(row.children) &&
                      row.children.length > 0;
                    const isExpanded = !!expandedRows[row._rowKey];
                    return (
                      <tr
                        key={row._rowKey}
                        className={idx % 2 ? "dtbs-row-odd" : "dtbs-row-even"}
                        style={
                          row._isChild ? { background: "#fff" } : undefined
                        }
                      >
                        <td
                          className="dtbs-td-center"
                          style={{
                            ...priceRangeTableStyles.bodyCell,
                            backgroundColor: row._isChild
                              ? "rgb(233 233 233)"
                              : undefined,
                          }}
                        >
                          {" "}
                          {row._isChild ? "" : t(row._rowIndex)}
                        </td>
                        <td
                          className="dtbs-td-left"
                          colSpan={2}
                          style={{
                            ...priceRangeTableStyles.bodyCell,
                            borderRight: "1px solid rgb(233 233 233)",
                          }}
                        >
                          {row._isChild ? (
                            <span style={{ paddingLeft: 14, color: "#606060" }}>
                              {t(row.itemName)}
                            </span>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: hasChildren ? "pointer" : "default",
                              }}
                              onClick={() =>
                                hasChildren && toggleRow(row._rowKey)
                              }
                            >
                              <span>{row.priceRange}</span>
                              {hasChildren ? (
                                <span
                                  style={{ color: "#b3b3b3", fontSize: 11 }}
                                >
                                  {isExpanded ? "▲" : "▼"}
                                </span>
                              ) : null}
                            </div>
                          )}
                        </td>
                        {/* <td
                          className="dtbs-td-right"
                          style={priceRangeTableStyles.bodyCell}
                        >
                          {formatNum(row.totalSales)}
                        </td> */}
                        {activeMonthKeys.map((k) => (
                          <td
                            key={`${row._rowKey}-${k}`}
                            className="dtbs-td-center"
                            style={priceRangeTableStyles.bodyCell}
                          >
                            {formatMonthQty(t(row.months?.[k]))}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {displayRows.length > 0 && (
            <div className="dtbs-pagination">
              <span className="dtbs-page-info">
                {/* {totalRecords === 0 ? "0 results" : `${rangeStart}-${rangeEnd} of ${totalRecords}`} */}
              </span>
              <div className="dtbs-page-controls">
                {/* <span className="dtbs-page-label">Rows per page:</span> */}
                {/* <select
                  className="dtbs-page-select"
                  value={limit}
                  onChange={(e) => {
                    const nextLimit = Number(e.target.value);
                    goToPage(1, nextLimit);
                  }}
                >
                  {[10, 16, 25, 40, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select> */}
                <span
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <select
                    className="dtbs-page-select"
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      goToPage(1, newSize);
                    }}
                    style={{
                      paddingRight: 24,
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                    }}
                  >
                    {[10, 16, 25, 40, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <KeyboardArrowDownIcon
                    fontSize="small"
                    style={{
                      position: "absolute",
                      right: 6,
                      pointerEvents: "none",
                      color: "#8a8a8a",
                    }}
                  />
                </span>
                <span className="dtbs-page-text">
                  {/* Page {page} of {totalPages} */}
                </span>
                {/* <button className="dtbs-nav-btn" onClick={() => goToPage(1)} disabled={page === 1}>
                  <KeyboardDoubleArrowLeftIcon />
                </button> */}
                <button
                  className="dtbs-nav-btn"
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                >
                  <KeyboardArrowLeftIcon />
                </button>
                <button
                  className="dtbs-nav-btn"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <KeyboardArrowRightIcon />
                </button>
                {/* <button
                  className="dtbs-nav-btn"
                  onClick={() => goToPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  <KeyboardDoubleArrowRightIcon />
                </button> */}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PriceRange;
